/**
 * Delivery Selector Module
 * 
 * Manages delivery preferences (Delivery vs Store pickup) with localStorage,
 * cart attribute sync, and event broadcasting. Provides backward compatibility
 * with old format.
 * 
 * New format:
 * - Key: 'deliveryPreference'
 * - Value: { deliveryType: 'DELIVERY'|'STORE', deliveryLocation: string|null, storeData: Object|null, corporateAddress: Object|null }
 * - storeData (when STORE): { store_name, handle, id, stockist_id, is_available } from metaobjects
 * - corporateAddress (when DELIVERY + corporate): { organisationHandle, addressName, address }
 * 
 * Cart attribute (synced on every preference change):
 * - Key: 'delivery_preference'
 * - Value: 'delivery', 'store|{store_handle}|{stockist_id}', or 'delivery|{org_handle}|{addressName}'
 * 
 * Old format (migrated automatically):
 * - Key: 'selectedStore'
 * - Value: 'Online' | '{Store Name}'
 */

(function() {
  'use strict';

  const NEW_STORAGE_KEY = 'deliveryPreference';
  const OLD_STORAGE_KEY = 'selectedStore';

  /**
   * Sync delivery preference to Shopify cart attribute.
   * Format: 'delivery', 'store|{store_handle}|{stockist_id}', or 'delivery|{org_handle}|{addressName}'
   * @param {string} deliveryType - 'DELIVERY' or 'STORE'
   * @param {Object|null} storeData - Store metaobject data
   * @param {Object|null} corporateAddress - Corporate address data { organisationHandle, addressName }
   */
  function updateCartAttribute(deliveryType, storeData, corporateAddress) {
    var value;
    if (deliveryType === 'DELIVERY' && corporateAddress && corporateAddress.organisationHandle) {
      value = 'delivery|' + corporateAddress.organisationHandle + '|' + corporateAddress.addressName;
    } else if (deliveryType === 'STORE' && storeData && storeData.stockist_id) {
      value = storeData.handle
        ? 'store|' + storeData.handle + '|' + storeData.stockist_id
        : 'store|' + storeData.stockist_id;
    } else {
      value = 'delivery';
    }

    fetch('/cart/update.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ attributes: { delivery_preference: value } })
    }).catch(function(error) {
      console.error('Failed to update cart delivery_preference attribute:', error);
    });
  }

  /**
   * Migrate from old format to new format
   * @returns {Object|null} Migrated preference or null
   */
  function migrateFromOldFormat() {
    const oldValue = localStorage.getItem(OLD_STORAGE_KEY);
    if (!oldValue) return null;

    let newPreference;
    if (oldValue === 'Online') {
      newPreference = {
        deliveryType: 'DELIVERY',
        deliveryLocation: null,
        storeData: null
      };
    } else if (typeof oldValue === 'string' && oldValue.trim() !== '') {
      newPreference = {
        deliveryType: 'STORE',
        deliveryLocation: oldValue.trim(),
        storeData: null
      };
    } else {
      // Unknown format, default to delivery
      newPreference = {
        deliveryType: 'DELIVERY',
        deliveryLocation: null,
        storeData: null
      };
    }

    // Save in new format and remove old key
    localStorage.setItem(NEW_STORAGE_KEY, JSON.stringify(newPreference));
    localStorage.removeItem(OLD_STORAGE_KEY);

    return newPreference;
  }

  /**
   * Get delivery preference from localStorage
   * @returns {Object} Preference object { deliveryType, deliveryLocation }
   */
  function getDeliveryPreference() {
    try {
      const stored = localStorage.getItem(NEW_STORAGE_KEY);
      
      if (stored) {
        const preference = JSON.parse(stored);
        // Validate structure
        if (preference && 
            (preference.deliveryType === 'DELIVERY' || preference.deliveryType === 'STORE') &&
            (preference.deliveryLocation === null || typeof preference.deliveryLocation === 'string')) {
          if (!preference.hasOwnProperty('storeData')) {
            preference.storeData = null;
          }
          if (!preference.hasOwnProperty('corporateAddress')) {
            preference.corporateAddress = null;
          }
          return preference;
        }
      }

      // Try migration from old format
      const migrated = migrateFromOldFormat();
      if (migrated) {
        return migrated;
      }

      // Default to delivery
      return {
        deliveryType: 'DELIVERY',
        deliveryLocation: null,
        storeData: null,
        corporateAddress: null
      };
    } catch (error) {
      console.error('Error getting delivery preference:', error);
      return {
        deliveryType: 'DELIVERY',
        deliveryLocation: null,
        storeData: null,
        corporateAddress: null
      };
    }
  }

  /**
   * Set delivery preference in localStorage and broadcast event
   * @param {string} deliveryType - 'DELIVERY' or 'STORE'
   * @param {string|null} deliveryLocation - Store location (store_name) or null for delivery
   * @param {Object|null} storeData - Store metaobject data { store_name, handle, id, stockist_id, is_available }
   * @param {Object|null} corporateAddress - Corporate address data { organisationHandle, addressName, address }
   * @param {Object} [options] - { revert: true } marks this write as a correction
   *   after a change that failed. Listeners that rewrite the cart skip it;
   *   listeners that only reflect the preference in the UI still act on it.
   *   Omit it and the write behaves exactly as it always has.
   */
  function setDeliveryPreference(deliveryType, deliveryLocation, storeData, corporateAddress, options) {
    try {
      // Validate inputs
      if (deliveryType !== 'DELIVERY' && deliveryType !== 'STORE') {
        throw new Error('deliveryType must be "DELIVERY" or "STORE"');
      }

      if (deliveryType === 'STORE' && !deliveryLocation) {
        throw new Error('deliveryLocation is required when deliveryType is "STORE"');
      }

      if (deliveryType === 'DELIVERY') {
        deliveryLocation = null;
      }

      // Get current preference BEFORE updating (for event detail)
      const oldPreference = getDeliveryPreference();

      const preference = {
        deliveryType: deliveryType,
        deliveryLocation: deliveryLocation,
        storeData: deliveryType === 'STORE' ? (storeData || null) : null,
        corporateAddress: (deliveryType === 'DELIVERY' && corporateAddress) ? corporateAddress : null
      };

      // Save to localStorage
      localStorage.setItem(NEW_STORAGE_KEY, JSON.stringify(preference));

      // Also update old format for backward compatibility
      const oldFormatValue = deliveryType === 'DELIVERY' ? 'Online' : deliveryLocation;
      localStorage.setItem(OLD_STORAGE_KEY, oldFormatValue);

      // A revert puts back the option that was in force before a change that
      // failed. Every listener still hears it, but the ones that rewrite the cart
      // use this flag to stay out of the way.
      const isRevert = !!(options && options.revert === true);

      // Broadcast event with both old and new values
      document.dispatchEvent(new CustomEvent('deliveryPreference:changed', {
        detail: {
          oldPreference: oldPreference,
          newPreference: preference,
          oldStore: oldPreference.deliveryType === 'DELIVERY' ? 'Online' : oldPreference.deliveryLocation,
          newStore: oldFormatValue,
          storeData: preference.storeData,
          corporateAddress: preference.corporateAddress,
          revert: isRevert
        }
      }));

      updateCartAttribute(deliveryType, preference.storeData, preference.corporateAddress);

      return true;
    } catch (error) {
      console.error('Error setting delivery preference:', error);
      return false;
    }
  }

  /**
   * Get display text for current preference
   * Attempts to use text from DOM (localized), falls back to hardcoded English
   * @returns {string} Display text (e.g., "Delivery" or "Store 2")
   */
  function getDisplayText() {
    const preference = getDeliveryPreference();
    if (preference.deliveryType === 'DELIVERY') {
      if (preference.corporateAddress && preference.corporateAddress.addressName) {
        return preference.corporateAddress.addressName;
      }
      const deliveryButton = document.querySelector('.delivery-type-button[data-delivery-type="DELIVERY"] .delivery-type-button__text');
      return deliveryButton ? deliveryButton.textContent.trim() : 'Delivery';
    } else {
      if (preference.storeData && preference.storeData.store_name) {
        return preference.storeData.store_name;
      }
      return preference.deliveryLocation || 'Store';
    }
  }

  /**
   * Get the full store metaobject data from the current preference
   * Returns the store_name, handle, id, and is_available from the metaobject
   * @returns {Object|null} Store data object or null if no store selected
   */
  function getStoreData() {
    const preference = getDeliveryPreference();
    return preference.storeData || null;
  }

  /**
   * Get store name in old format (for backward compatibility)
   * Used by store-variant-filter.js and cart-store-switcher.js
   * @returns {string} Store name ('Online' or any store label)
   */
  function getSelectedStore() {
    const preference = getDeliveryPreference();
    if (preference.deliveryType === 'DELIVERY') {
      return 'Online';
    } else {
      return preference.deliveryLocation || 'Online';
    }
  }

  // Export to global scope
  window.DeliverySelector = {
    getDeliveryPreference: getDeliveryPreference,
    setDeliveryPreference: setDeliveryPreference,
    getDisplayText: getDisplayText,
    getSelectedStore: getSelectedStore,
    getStoreData: getStoreData
  };

  // Initialize: Perform migration on page load if needed
  (function init() {
    const hasNewPreference = localStorage.getItem(NEW_STORAGE_KEY) !== null;
    const preference = getDeliveryPreference();

    // Keep old format in sync only when a real new-format value exists.
    // This avoids creating selectedStore='Online' for first-time visitors,
    // which can later migrate and appear as an unwanted auto-filled preference.
    if (hasNewPreference) {
      const oldValue = localStorage.getItem(OLD_STORAGE_KEY);
      const expectedOldValue = preference.deliveryType === 'DELIVERY' ? 'Online' : preference.deliveryLocation;
      if (oldValue !== expectedOldValue) {
        localStorage.setItem(OLD_STORAGE_KEY, expectedOldValue);
      }
    }

    // Sync cart attribute on every page load so checkout always has the current preference,
    // regardless of whether the user is logged in or not.
    updateCartAttribute(preference.deliveryType, preference.storeData, preference.corporateAddress);
  })();

})();
